import type { CopyId } from '../../../copy'
import type {
  AiOsEvaluationOutcome,
  AiOsLearningState,
  AiOsModuleState,
  AiOsProposalState,
  AiOsRisk,
  AiOsVerification,
} from '../../../app/ai-os'
import { T } from '../../../copy/text'
import { Tag, type TagTone } from '../../../components/tag'

/**
 * 模块演进那些**枚举**在界面上说哪一句、用什么颜色。
 *
 * # 词来自模板，颜色是这一层的判断
 *
 * 每张表都是 `Record<枚举, CopyId>`，**逐条写死、不拼接**：
 *
 *   拼出来的 id（`` `ai_os.risk.${risk}` ``）绕开编译期保证——两边任一侧
 *   错一个字母，四道检查全过，到用户那儿变成一句空话（`tools/check-copy-ids.mjs`）。
 *   写成 `Record<AiOsRisk, CopyId>` 之后，少一档 `tsc` 当场红，
 *   id 打错字也当场红（不在 `CopyId` 联合里）。
 *
 * 颜色是这一层自己定的，而且有依据：绿黄红三档的语义在蓝图 §5，
 * 「看起来和能用一样」那个坑 `vendor/capability.tsx` 踩过一次。
 *
 * # 两处刻意的取舍
 *
 * **`candidate` 是中性不是绿。** 它只表示「跑过」，而跑过的东西看起来
 * 和验过的一样——给它一个和 `production_verified` 相同的颜色，
 * 就等于在界面上把三档压回一档，而那正是蓝图 §3.3 分这三档要防的事。
 *
 * **`rolled_back` 是红不是灰。** 「退回去了」与「还没晋升」不是一回事。
 */

const RISK_TONE: Record<AiOsRisk, TagTone> = { green: 'ok', amber: 'warn', red: 'danger' }

const RISK_WORD: Record<AiOsRisk, CopyId> = {
  green: 'ai_os.risk.green',
  amber: 'ai_os.risk.amber',
  red: 'ai_os.risk.red',
}


export function RiskTag({ risk }: { risk: AiOsRisk }) {
  return (
    <Tag tone={RISK_TONE[risk]} data-testid="ai-os-risk" data-raw={risk}>
      <T id={RISK_WORD[risk]} />
    </Tag>
  )
}

const VERIFICATION_TONE: Record<AiOsVerification, TagTone> = {
  candidate: 'neutral',
  preview_verified: 'info',
  production_verified: 'ok',
}

const VERIFICATION_WORD: Record<AiOsVerification, CopyId> = {
  candidate: 'ai_os.evidence.candidate',
  preview_verified: 'ai_os.evidence.preview_verified',
  production_verified: 'ai_os.evidence.production_verified',
}


export function VerificationTag({ verification }: { verification: AiOsVerification }) {
  return (
    <Tag tone={VERIFICATION_TONE[verification]} data-testid="ai-os-verification" data-raw={verification}>
      <T id={VERIFICATION_WORD[verification]} />
    </Tag>
  )
}

/** `pending` 是中性：它不是「暂时还行」，是「什么都还不知道」。 */
const OUTCOME_TONE: Record<AiOsEvaluationOutcome, TagTone> = {
  pending: 'neutral',
  passed: 'ok',
  failed: 'danger',
  degraded: 'warn',
}

const OUTCOME_WORD: Record<AiOsEvaluationOutcome, CopyId> = {
  pending: 'ai_os.eval.pending',
  passed: 'ai_os.eval.passed',
  failed: 'ai_os.eval.failed',
  degraded: 'ai_os.eval.degraded',
}

export function OutcomeTag({ outcome }: { outcome: AiOsEvaluationOutcome }) {
  return (
    <Tag tone={OUTCOME_TONE[outcome]} data-testid="ai-os-outcome" data-raw={outcome}>
      <T id={OUTCOME_WORD[outcome]} />
    </Tag>
  )
}

const MODULE_STATE_TONE: Record<AiOsModuleState, TagTone> = {
  active: 'ok',
  deprecated: 'warn',
  retired: 'neutral',
}

const MODULE_STATE_WORD: Record<AiOsModuleState, CopyId> = {
  active: 'ai_os.module.state.active',
  deprecated: 'ai_os.module.state.deprecated',
  retired: 'ai_os.module.state.retired',
}

export function ModuleStateTag({ state }: { state: AiOsModuleState }) {
  return (
    <Tag tone={MODULE_STATE_TONE[state]} data-testid="ai-os-module-state" data-raw={state}>
      <T id={MODULE_STATE_WORD[state]} />
    </Tag>
  )
}

/**
 * 学习项的状态。
 *
 * `candidate` 与 `promoted` **没有各自的词**：契约里 `active` 那个布尔
 * 已经把「现在管不管用」说清了（`ai_os.learning.active`），
 * 而「还在观察」走的是提案那侧的 `ai_os.stage.learning_candidate`。
 * 硬给这两档各造一个词，会在同一行里出现两句说同一件事的话。
 */
const LEARNING_STATE_TONE: Record<AiOsLearningState, TagTone> = {
  candidate: 'neutral',
  promoted: 'ok',
  disabled: 'warn',
  expired: 'neutral',
  rolled_back: 'danger',
}

const LEARNING_STATE_WORD: Record<AiOsLearningState, CopyId | null> = {
  candidate: null,
  promoted: null,
  disabled: null, // 走 ai_os.learning.disabled，它要一个 {reason}
  expired: 'ai_os.learning.expired_state',
  rolled_back: 'ai_os.learning.rolled_back_state',
}

export function LearningStateTag({ state }: { state: AiOsLearningState }) {
  const id = LEARNING_STATE_WORD[state]
  if (!id) return null
  return (
    <Tag tone={LEARNING_STATE_TONE[state]} data-testid="ai-os-learning-state" data-raw={state}>
      <T id={id} />
    </Tag>
  )
}

/**
 * 提案走到哪一步了，**大白话**。
 *
 * # 优先用后端那一句
 *
 * 契约给了 `ImprovementProposal.stage_label`，说明写着它由 `docs/copy/`
 * 的模板渲染。**后端给了就用后端的**——它比这边多知道一些事
 * （比如卡在哪个步骤上）。
 *
 * # 后端没给的时候按 state 取同一批模板
 *
 * 这不是「两份真相」：**取的是同一批模板**（`ai_os.stage.<state>`），
 * 而且这张表逐条写死、`Record<AiOsProposalState, CopyId>` 保证十四档一个不少，
 * 少一档 `tsc` 当场红。两侧渲染出来的是同一句话。
 *
 * 反过来做才是两份真相：**前端自己判断「什么情况算什么阶段」**。
 * 这里没有任何判断——state 是接口给的，模板名与 state 一一对应。
 */
const STAGE_WORD: Record<AiOsProposalState, CopyId> = {
  observed: 'ai_os.stage.observed',
  diagnosed: 'ai_os.stage.diagnosed',
  proposed: 'ai_os.stage.proposed',
  accepted: 'ai_os.stage.accepted',
  executing: 'ai_os.stage.executing',
  preview_verified: 'ai_os.stage.preview_verified',
  releasing: 'ai_os.stage.releasing',
  production_verified: 'ai_os.stage.production_verified',
  learning_candidate: 'ai_os.stage.learning_candidate',
  promoted: 'ai_os.stage.promoted',
  failed: 'ai_os.stage.failed',
  fix_requested: 'ai_os.stage.fix_requested',
  rollback_requested: 'ai_os.stage.rollback_requested',
  rolled_back: 'ai_os.stage.rolled_back',
}

export function StageLine({
  state,
  stageLabel,
}: {
  state: AiOsProposalState
  stageLabel?: string
}) {
  return (
    <span data-testid="ai-os-stage" className="text-npc-caption text-npc-ink-medium">
      {stageLabel ? stageLabel : <T id={STAGE_WORD[state]} />}
    </span>
  )
}
